import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'react/components/Button';
import TextInput from 'react/elements/TextInput';
import MaterialIcon from 'react/elements/MaterialIcon';
import RecipeCard from './RecipeCard';
import MealIdeaIngredientList from './MealIdeaIngredientList';
import ingredientsOnList from './ingredientsOnList';
import ingredientsLastBought from './ingredientsLastBought';
import GroceryItemEditor from 'react/components/groceryItems/GroceryItemEditor';
import { findIdeaNamed } from 'react/components/calendar/searchMealIdeas';
import { addMealIdeaToActiveList, removeIngredientFromActiveList, updateMealIdea } from 'redux/actions/mealIdeas';
import { selectActiveListRows, selectMealIdeasIndex } from '../../../redux/food/selectors';
import useNowMinute from '../../hooks/useNowMinute';
import useOffline, { NEEDS_CONNECTION_TITLE, onlineOnlyField, useSaveWhenOnline } from '../../hooks/useOffline';

/**
 * The open idea: name, recipe link, ingredients and notes. Fields save when they lose focus. The
 * parent keys this by idea id, so switching ideas starts from fresh field state rather than
 * carrying one idea's draft into another.
 *
 * "Add to grocery list" puts the idea's ingredients on the active grocery list, or only the picked
 * ones when any are, and then clears the picking. Adding twice is safe: ingredients already there
 * from this idea are skipped, and when that is all of them the button has nothing to do.
 * An ingredient's cart takes it back off the list, where this idea is all that holds it there.
 *
 * A rename to a name another idea already has, ignoring case and spacing, is put back with a note
 * saying which, rather than sent for the server to refuse.
 *
 * It is shown in a popup, and `onClose` is the close icon, or, given `backTo` naming what it goes
 * back to, a back arrow, for an idea opened in place of something else rather than over it. Given
 * `onOpenIngredient`, an ingredient's name goes to it, with the ingredient's id, rather than opening
 * the item editor here. An ingredient's name opens it in the item
 * editor (GroceryItemEditor), which takes the popup's place, with the idea's amount and notes for it,
 * until its back arrow returns here.
 *
 * Opened from a meal on a calendar day, `removal` is that meal, its slot, and `onRemove` taking it
 * off the day (resolving whether it did). The footer then offers that too, asking first.
 *
 * Every change here but that removal is online-only, so offline the fields are read-only and the add
 * disabled, titled "Needs a connection"; a field left offline saves once the connection is back.
 * Removing the meal is an op and works offline.
 */
export default function MealIdeaPanel({ idea, onClose, removal = null, backTo = null, onOpenIngredient = null }) {
	const dispatch = useDispatch();
	const [name, setName] = useState(idea.name || '');
	const [notes, setNotes] = useState(idea.notes || '');
	const [adding, setAdding] = useState(false);
	const [addResult, setAddResult] = useState(null);
	const [nameError, setNameError] = useState(null);
	const [confirmingRemove, setConfirmingRemove] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [picked, setPicked] = useState(() => new Set());
	const [unlisting, setUnlisting] = useState(() => new Set());
	// The ingredient open in the item editor, which takes the panel's place until it is closed.
	const [openIngredientId, setOpenIngredientId] = useState(null);
	const minute = useNowMinute();
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const activeRows = useSelector((state) => selectActiveListRows(state.food.entities, minute));
	const entities = useSelector((state) => state.food.entities);
	const mounted = useRef(true);
	const offline = useOffline();
	const whenOnline = useSaveWhenOnline(offline);

	useEffect(() => () => {
		mounted.current = false;
	}, []);

	const ingredients = idea.ingredients || [];
	const hasIngredients = ingredients.length > 0;
	const onList = ingredientsOnList(ingredients, activeRows, ideasById);
	const lastBought = ingredientsLastBought(ingredients, entities);
	const allOnList = hasIngredients && ingredients.every((ingredient) => onList.get(ingredient.id)?.linked);
	// An ingredient removed, or added to the list by someone else, drops out of the picking.
	const pickedIds = ingredients
		.filter((ingredient) => picked.has(ingredient.id) && !onList.get(ingredient.id)?.linked)
		.map((ingredient) => ingredient.id);

	const addToGroceryList = () => {
		setAdding(true);
		setAddResult(null);
		dispatch(addMealIdeaToActiveList(idea.id, pickedIds.length ? pickedIds : null)).then((succeeded) => {
			if(!mounted.current) {
				return;
			}
			setAdding(false);
			if(succeeded) {
				setPicked(new Set());
			}
			setAddResult(succeeded ?
				{ ok: true, text: 'Added to the grocery list.' } :
				{ ok: false, text: 'That did not reach the grocery list. Try again.' });
		});
	};

	const takeOffList = (ingredient) => {
		setAddResult(null);
		setUnlisting((ids) => new Set(ids).add(ingredient.id));
		dispatch(removeIngredientFromActiveList(idea.id, ingredient.id)).then((succeeded) => {
			if(!mounted.current) {
				return;
			}
			setUnlisting((ids) => {
				const next = new Set(ids);
				next.delete(ingredient.id);
				return next;
			});
			if(!succeeded) {
				setAddResult({ ok: false, text: `${ingredient.name} did not come off the grocery list. Try again.` });
			}
		});
	};

	const removeMeal = () => {
		setRemoving(true);
		setAddResult(null);
		Promise.resolve(removal.onRemove()).then((removed) => {
			if(!mounted.current) {
				return;
			}
			setRemoving(false);
			setConfirmingRemove(false);
			if(!removed) {
				setAddResult({ ok: false, text: `That did not come off ${removal.slot.name}. Try again.` });
			}
		});
	};

	const saveName = () => {
		const trimmed = name.trim();
		if(!trimmed) {
			setName(idea.name);
			return;
		}
		const others = Object.values(ideasById).filter((other) => other.id !== idea.id);
		const taken = findIdeaNamed(others, trimmed);
		if(taken) {
			setName(idea.name);
			setNameError(`There is already a meal idea called \u201C${taken.name}\u201D.`);
			return;
		}
		if(trimmed !== idea.name) {
			dispatch(updateMealIdea(idea.id, { name: trimmed }));
		}
	};

	const saveNotes = () => {
		if(notes !== (idea.notes || '')) {
			dispatch(updateMealIdea(idea.id, { notes }));
		}
	};

	if(openIngredientId !== null && ingredients.some((ingredient) => ingredient.id === openIngredientId)) {
		return <GroceryItemEditor idea={idea} ingredientId={openIngredientId} onClose={() => setOpenIngredientId(null)} />;
	}

	return (
		<div className='meal_idea_panel'>
			<div className='panel_heading'>
				<div className='input-field meal_idea_panel__name'>
					<TextInput
						aria-label='Name'
						{...onlineOnlyField(offline)}
						value={name}
						onChange={(event) => {
							setName(event.target.value);
							setNameError(null);
						}}
						onBlur={() => whenOnline('name', saveName)}
						onKeyDown={(event) => {
							if(event.key === 'Enter') {
								event.preventDefault();
								whenOnline('name', saveName);
							}
						}}
					/>
				</div>
				{backTo ?
				<button type='button' className='btn-flat meal_idea_panel__icon' title={`Back to ${backTo}`} aria-label={`Back to ${backTo}`} onClick={onClose}>
					<MaterialIcon>arrow_back</MaterialIcon>
				</button> :
				<button type='button' className='btn-flat meal_idea_panel__icon' title='Close' aria-label='Close' onClick={onClose}>
					<MaterialIcon>close</MaterialIcon>
				</button>
				}
			</div>

			{!!nameError && <p className='meal_idea_panel__name-error'>{nameError}</p>}

			<div className='meal_idea_panel__body'>
				<section className='meal_idea_panel__section'>
					<h6>Recipe</h6>
					<RecipeCard idea={idea} />
				</section>

				<section className='meal_idea_panel__section'>
					<h6>Ingredients</h6>
					<MealIdeaIngredientList
						idea={idea}
						selected={picked}
						onSelect={(next) => {
							setPicked(next);
							setAddResult(null);
						}}
						onList={onList}
						lastBought={lastBought}
						onOpen={(ingredient) => (onOpenIngredient ? onOpenIngredient(ingredient.id) : setOpenIngredientId(ingredient.id))}
						onUnlist={takeOffList}
						unlisting={unlisting}
					/>
				</section>

				<section className='meal_idea_panel__section'>
					<h6>Notes</h6>
					<div className='input-field'>
						<TextInput
							textarea
							aria-label='Notes'
							{...onlineOnlyField(offline)}
							placeholder='Anything worth remembering'
							value={notes}
							onChange={(event) => setNotes(event.target.value)}
							onBlur={() => whenOnline('notes', saveNotes)}
						/>
					</div>
				</section>
			</div>

			{!!removal && confirmingRemove &&
			<div className='meal_idea_panel__confirm' role='alertdialog' aria-label={`Remove ${removal.meal.name}`}>
				<p>Remove {removal.meal.name} from {removal.slot.name}?</p>
				<Button flat className='meal_idea_panel__confirm-cancel' onClick={() => setConfirmingRemove(false)}>
					Cancel
				</Button>
				<Button flat className='meal_idea_panel__confirm-remove' disabled={removing} onClick={removeMeal}>
					{removing ? 'Removing...' : 'Remove'}
				</Button>
			</div>
			}

			{/*
			  * The buttons share one row however narrow the screen: Remove says only that, the slot it
			  * leaves being in its tooltip and in the question it asks first. What happened sits above them.
			  */}
			<div className='meal_idea_panel__footer'>
				{!!addResult &&
				<p className={`meal_idea_panel__status ${addResult.ok ? '' : 'is-error'}`}>{addResult.text}</p>
				}
				<div className='meal_idea_panel__actions'>
					{!!removal &&
					<Button
						flat
						className='meal_idea_panel__remove'
						disabled={removing || confirmingRemove}
						title={`Remove from ${removal.slot.name}`}
						aria-label={`Remove from ${removal.slot.name}`}
						onClick={() => setConfirmingRemove(true)}
					>
						<MaterialIcon className='left'>delete</MaterialIcon>
						Remove
					</Button>
					}
					<Button
						flat
						className={pickedIds.length ? 'meal_idea_panel__add is-picked' : 'meal_idea_panel__add'}
						disabled={offline || adding || !hasIngredients || allOnList}
						title={offline ? NEEDS_CONNECTION_TITLE : addTitle(hasIngredients, allOnList, pickedIds.length)}
						onClick={addToGroceryList}
					>
						<MaterialIcon className='left'>add_shopping_cart</MaterialIcon>
						<span className='meal_idea_panel__add-label'>{adding ? 'Adding...' : addLabel(pickedIds.length)}</span>
					</Button>
				</div>
			</div>
		</div>
	);
}

/** "Add 2 to grocery list", with "grocery " in a span a narrow screen hides, leaving "Add 2 to list". */
function addLabel(pickedCount) {
	return (
		<React.Fragment>
			{pickedCount === 0 ? 'Add to ' : `Add ${pickedCount} to `}
			<span className='meal_idea_panel__add-optional'>grocery </span>
			list
		</React.Fragment>
	);
}

function addTitle(hasIngredients, allOnList, pickedCount) {
	if(!hasIngredients) {
		return 'Add an ingredient first';
	}
	if(allOnList) {
		return 'Everything here is already on the grocery list';
	}
	return pickedCount ? 'Put the picked ingredients on the grocery list' : 'Put these ingredients on the grocery list';
}
